import { useState } from 'react';
import type { WeeklyPlan, PlanFeedback } from '../types';

interface Props {
  plan: WeeklyPlan;
  myFeedback: PlanFeedback | null;
  submitting: boolean;
  onSubmit: (status: 'approved' | 'concerns', comment?: string) => void;
}

export default function PlanFeedbackPanel({ plan, myFeedback, submitting, onSubmit }: Props) {
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [comment, setComment] = useState('');

  if (plan.status !== 'pending_review') return null;

  // Already submitted feedback
  if (myFeedback) {
    return (
      <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white">Driving Plan Review</h3>
          <span className="text-[10px] font-bold uppercase bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded-full">
            In Review
          </span>
        </div>

        {/* Show assignments */}
        <div className="space-y-2 mb-3">
          {plan.assignments.map(a => (
            <div key={a.eventId} className="flex items-center justify-between text-xs bg-gray-50 dark:bg-gray-800/50 rounded-lg px-3 py-2">
              <span className="font-mono text-gray-500 dark:text-gray-400 w-20">
                {new Date(a.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' })}
              </span>
              <div className="flex-1 min-w-0 text-right">
                {a.pickupDriverId ? (
                  <span className="text-gray-700 dark:text-gray-300">
                    <span className="font-semibold">{a.pickupDriverName || 'Assigned'}</span>
                    {a.returnDriverId && a.returnDriverId !== a.pickupDriverId && (
                      <span className="text-gray-400"> / {a.returnDriverName || 'Return'}</span>
                    )}
                  </span>
                ) : (
                  <span className="text-amber-500 font-semibold">Needs driver</span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className={`flex items-center gap-2 text-sm font-semibold rounded-lg px-3 py-2 ${
          myFeedback.status === 'approved'
            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
        }`}>
          <span>{myFeedback.status === 'approved' ? '\u2705' : '\u26A0\uFE0F'}</span>
          <span>
            {myFeedback.status === 'approved' ? 'You approved this plan' : 'You flagged concerns'}
          </span>
        </div>
        {myFeedback.comment && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 pl-1">"{myFeedback.comment}"</p>
        )}
      </div>
    );
  }

  // Needs to submit feedback
  return (
    <div className="bg-white dark:bg-gray-900 border border-blue-200 dark:border-blue-900/40 rounded-xl p-4 mb-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-gray-900 dark:text-white">Driving Plan Review</h3>
        <span className="text-[10px] font-bold uppercase bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded-full">
          In Review
        </span>
      </div>
      <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">
        Review the proposed driving assignments and share your feedback.
      </p>

      {/* Show assignments */}
      <div className="space-y-2 mb-3">
        {plan.assignments.map(a => (
          <div key={a.eventId} className="flex items-center justify-between text-xs bg-gray-50 dark:bg-gray-800/50 rounded-lg px-3 py-2">
            <span className="font-mono text-gray-500 dark:text-gray-400 w-20">
              {new Date(a.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' })}
            </span>
            <div className="flex-1 min-w-0 text-right">
              {a.pickupDriverId ? (
                <span className="text-gray-700 dark:text-gray-300">
                  <span className="font-semibold">{a.pickupDriverName || 'Assigned'}</span>
                  {a.returnDriverId && a.returnDriverId !== a.pickupDriverId && (
                    <span className="text-gray-400"> / {a.returnDriverName || 'Return'}</span>
                  )}
                </span>
              ) : (
                <span className="text-amber-500 font-semibold">Needs driver</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {showCommentInput ? (
        <div className="space-y-2">
          <textarea
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="What's your concern?"
            className="w-full text-sm border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-300 dark:focus:ring-red-700 resize-none"
            rows={2}
            autoFocus
          />
          <div className="flex gap-2">
            <button
              onClick={() => { setShowCommentInput(false); setComment(''); }}
              className="flex-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-sm font-semibold py-2 rounded-lg transition-colors min-h-[44px]"
            >
              Cancel
            </button>
            <button
              onClick={() => onSubmit('concerns', comment)}
              disabled={submitting}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold py-2 rounded-lg transition-colors disabled:opacity-50 min-h-[44px]"
            >
              {submitting ? 'Sending...' : 'Submit Concern'}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <button
            onClick={() => onSubmit('approved')}
            disabled={submitting}
            className="flex-1 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-50 min-h-[44px] flex items-center justify-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.633 10.5c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 012.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 00.322-1.672V3.75A.75.75 0 0114.25 3a2.25 2.25 0 012.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 01-2.649 7.521c-.388.482-.987.729-1.605.729H14.23c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 00-1.423-.23H5.904M14.25 9h2.25M5.904 18.75c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 01-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 10.203 4.167 9.75 5 9.75h1.053c.472 0 .745.556.5.96a8.958 8.958 0 00-1.302 4.665c0 1.194.232 2.333.654 3.375z" />
            </svg>
            {submitting ? 'Sending...' : 'Looks Good'}
          </button>
          <button
            onClick={() => setShowCommentInput(true)}
            className="flex-1 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 text-sm font-semibold py-2.5 rounded-lg transition-colors min-h-[44px] flex items-center justify-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            Has Concerns
          </button>
        </div>
      )}
    </div>
  );
}
